import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SSOBaseService } from './sso-base.service';
import { SSOAuthResponse, SSOProvider, SSOConfig } from '../interfaces/sso.interface';
import { loadAppleSsoScript } from './sso-scripts';

export interface AppleAuthResponse {
  authorization: {
    code: string;
    id_token: string;
    state: string;
  };
  user?: {
    email?: string;
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class AppleSsoService extends SSOBaseService {
  protected provider = SSOProvider.APPLE;
  protected isInitialized = false;
  private clientId: string = '';

  constructor() {
    super();
  }

  async initialize(config: SSOConfig): Promise<boolean> {
    try {
      this.clientId = config.clientId;

      if (!this.clientId) {
        console.error('Apple SSO: Client ID is missing');
        throw new Error('Apple client ID is required');
      }

      console.log('Apple SSO: Starting initialization with client ID:', this.clientId);

      // Load the Apple authentication script
      await loadAppleSsoScript({ scriptId: 'apple-auth-script' });

      const appleAuth = (window as any).AppleID;

      if (!appleAuth) {
        console.error('Apple SSO: Apple Sign-In SDK not available after script load');
        throw new Error('Apple Sign-In SDK not available');
      }

      console.log('Apple SSO: SDK loaded successfully, initializing auth...');

      // Initialize Apple authentication
      appleAuth.auth.init({
        clientId: this.clientId,
        scope: 'name email',
        redirectURI: this.getCurrentUrl(),
        usePopup: true,
        state: 'SignInUserAuthenticationRequest',
      });

      this.isInitialized = true;
      this.logEvent('initialized', { clientId: this.clientId });
      console.log('Apple SSO: Initialization completed successfully');
      return true;
    } catch (error) {
      console.error('Apple SSO: Initialization failed:', error);
      this.logEvent('initialization_failed', error);
      return false;
    }
  }

  authenticate(): Observable<SSOAuthResponse> {
    if (!this.isInitialized) {
      console.error('Apple SSO: Not initialized. Call initialize() first.');
      return this.handleError(new Error('Apple SSO not initialized. Call initialize() first.'), 'authentication');
    }

    return from(this.performAppleSignIn()).pipe(
      map((response) => response),
      catchError((error) => this.handleError(error, 'authentication'))
    );
  }

  isAvailable(): boolean {
    const available = this.isAppleAvailable() && this.isInitialized;
    console.log('Apple SSO: Available check:', {
      sdkAvailable: this.isAppleAvailable(),
      initialized: this.isInitialized,
      result: available,
    });
    return available;
  }

  /**
   * Legacy method for backward compatibility
   * @returns Observable with the Apple authentication response
   */
  signInWithApple(): Observable<AppleAuthResponse> {
    console.log('Apple SSO: signInWithApple called');
    if (!this.isInitialized) {
      console.error('Apple SSO: Not initialized for signInWithApple');
      throw new Error('Apple SSO not initialized. Call initialize() first.');
    }
    return from(this.performLegacyAppleSignIn());
  }

  private async performLegacyAppleSignIn(): Promise<AppleAuthResponse> {
    try {
      console.log('Apple SSO: Starting legacy sign-in process...');

      const appleAuth = (window as any).AppleID;

      if (!appleAuth) {
        throw new Error('Apple Sign-In SDK not available during sign-in');
      }

      console.log('Apple SSO: Calling appleAuth.auth.signIn() for legacy method...');

      // Perform sign-in
      const response = await appleAuth.auth.signIn();

      console.log('Apple SSO: Legacy sign-in response received:', response);

      return response as AppleAuthResponse;
    } catch (error) {
      console.error('Apple SSO: Legacy sign-in failed:', error);
      throw error;
    }
  }

  private async performAppleSignIn(): Promise<SSOAuthResponse> {
    try {
      console.log('Apple SSO: Starting sign-in process...');
      this.logEvent('sign_in_started');

      const appleAuth = (window as any).AppleID;

      if (!appleAuth) {
        throw new Error('Apple Sign-In SDK not available during sign-in');
      }

      console.log('Apple SSO: Calling appleAuth.auth.signIn()...');

      // Perform sign-in
      const response = await appleAuth.auth.signIn();

      console.log('Apple SSO: Sign-in response received:', response);

      this.logEvent('sign_in_successful', {
        hasUser: !!response.user,
        hasToken: !!response.authorization?.id_token,
      });

      // Create standardized user data
      const userData = this.createUserData(
        response.authorization.code, // Use code as ID since Apple doesn't provide a stable user ID
        response.user?.email,
        response.user?.name?.firstName,
        response.user?.name?.lastName,
        `${response.user?.name?.firstName || ''} ${response.user?.name?.lastName || ''}`.trim()
      );

      return {
        success: true,
        userData: userData,
        token: response.authorization.id_token,
      };
    } catch (error) {
      console.error('Apple SSO: Sign-in failed:', error);
      this.logEvent('sign_in_failed', error);
      throw error;
    }
  }

  private isAppleAvailable(): boolean {
    const available = !!(window as any).AppleID;
    console.log('Apple SSO: SDK availability check:', available);
    return available;
  }
}
